import type { ReactNode } from 'react';
import React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { AnnotationLabelT } from '../../../../../common/types/types';
import { getAccentColorForStroke } from '../../../../../common/utils/contrast-color';
import { EVENTS } from '../../../../labelling-v2/atoms/label-store-dispatch/events';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { TOOL } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import { EDITOR_MODE_TYPE } from '../../../types';
import { transformPoint } from '../rectangle/utils';
import { useRelationshipTargetValidation } from './hooks/useRelationshipTargetValidation';
import AnnotationLabel from './annotation-label';
import AnnotationMetadataPill from './annotation-metadata-pill';
import { type VertexT, Vertex, VERTEX_TYPE } from './vertex';
const DISABLE_ANNOTATION_OPACITY = 0.5;
type Props = {
  vertices: VertexT[];
  ghostVertices?: VertexT[];
  children: ReactNode;
  isLocked?: boolean;
  isDrawing?: boolean;
  shouldShowVertexDuringDrawing?: boolean;
  annotationIdx?: string;
  onVertexSelect?: (vertex: VertexT, index: number) => void;
  onVertexDeselect?: (vertex: VertexT, index: number) => void;
  onVertexEnter?: (vertex: VertexT, index: number) => void;
  onVertexLeave?: (vertex: VertexT, index: number) => void;
  annotationLabel: {
    hideLabelPointer?: boolean;
    labelCoords: [number, number] | undefined | null;
  } & AnnotationLabelT;
  rotationAngle?: number;
  rotationAllowed?: boolean;
  centerCoords?: [number, number];
  dataAttributes?: {
    [key: string]: string;
  };
  metadataPillText?: string;
};
const Shape = ({
  isDrawing,
  isLocked,
  shouldShowVertexDuringDrawing,
  vertices,
  children,
  annotationIdx,
  onVertexSelect,
  onVertexDeselect,
  onVertexEnter,
  onVertexLeave,
  annotationLabel,
  rotationAngle,
  rotationAllowed = false,
  centerCoords = [0, 0],
  ghostVertices,
  dataAttributes = {},
  metadataPillText
}: Props) => {
  const {
    getStageSettings
  } = useStage();
  const {
    editorModeAtom,
    selectedToolAtom,
    editableAnnotationAtom,
    dispatchAtom
  } = useStageAtoms();
  const dispatch = useSetAtom(dispatchAtom);
  const editorMode = useAtomValue(editorModeAtom);
  const selectedTool = useAtomValue(selectedToolAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const vertexStrokeColor = isVideoLabelingM4Enabled ? annotationLabel.style?.strokeColor : undefined;
  /**
   * Accent color paired with strokeColor across the annotation's rendering.
   * Stays visually distinct from strokeColor for any color, custom or preset.
   */
  const accentColor = getAccentColorForStroke(annotationLabel.style?.strokeColor ?? '');
  const namePillStyle = {
    ...annotationLabel.style,
    fillColor: accentColor
  };
  const vertexBorderColor = isVideoLabelingM4Enabled ? accentColor : undefined;
  const isAnnotationRelationshipMode = editorMode === EDITOR_MODE_TYPE.RELATIONSHIP;
  const isPointerSelected = selectedTool === TOOL.POINTER;
  const isSelected = editableAnnotation === annotationIdx;
  const {
    isInvalidRelationshipTarget,
    isRelationshipEditorOpen
  } = useRelationshipTargetValidation({
    annotationIdx
  });
  const scaleForRotationHandler = rotationAllowed ? getStageSettings().scale : 0;
  // @ts-expect-error todo(ts-migration) TS2322 Type '[number, number] | undefined' is not assignable to type '[number, number]'.
  let rotationHandlerCoords: [number, number] = rotationAllowed ? vertices.filter(vertex => vertex.type === VERTEX_TYPE.TOP).map<[number, number]>(rotationVertex => {
    return [rotationVertex.point[0], rotationVertex.point[1] - 20 / scaleForRotationHandler];
  })[0] : [0, 0];
  rotationHandlerCoords = transformPoint({
    point: rotationHandlerCoords,
    centerCoords,
    rotation: rotationAngle
  });
  vertices = vertices.map(vertex => ({
    ...vertex,
    point: transformPoint({
      point: vertex.point,
      centerCoords,
      rotation: rotationAngle
    })
  }));
  const metadataPillAnchor = vertices.find(vertex => vertex.type === VERTEX_TYPE.TOP_LEFT)?.point ?? vertices[0]?.point;
  const onShapeSelect = () => {
    if (annotationIdx && isPointerSelected && !isLocked) {
      dispatch({
        type: EVENTS.SELECT_ANNOTATION,
        annotationId: annotationIdx
      });
    }
  };
  const handleOnPointerDown = () => {
    if (editorMode === EDITOR_MODE_TYPE.LABELING) {
      onShapeSelect();
      return;
    }
    if (!annotationIdx) {
      return;
    }
    if (isRelationshipEditorOpen) {
      if (!isInvalidRelationshipTarget) {
        dispatch({
          type: EVENTS.SELECT_RELATIONSHIP_TARGET,
          annotationId: annotationIdx
        });
      }
      return;
    }
    onShapeSelect();
  };
  return <g onPointerDown={handleOnPointerDown} onMouseEnter={() => {
    if (isDrawing) {
      return;
    }
    dispatch({
      type: EVENTS.FOCUS_ANNOTATION,
      annotationId: annotationIdx || null
    });
  }} onMouseLeave={() => {
    if (isDrawing) {
      return;
    }
    dispatch({
      type: EVENTS.FOCUS_ANNOTATION,
      annotationId: null
    });
  }} style={{
    cursor: isSelected && isPointerSelected ? 'grab' : 'inherit',
    opacity: isInvalidRelationshipTarget ? DISABLE_ANNOTATION_OPACITY : 1,
    pointerEvents: isInvalidRelationshipTarget ? 'none' : 'auto'
  }} {...dataAttributes}>
      {children}

      {!isDrawing && annotationLabel.labelCoords && <AnnotationLabel label={isVideoLabelingM4Enabled && annotationIdx ? `${annotationLabel?.title ?? ''}  [${annotationIdx}]` : annotationLabel?.title ?? ''} labelCoords={annotationLabel.labelCoords} annotationStyle={namePillStyle} hideLabelPointer={annotationLabel.hideLabelPointer} />}

      {!isDrawing && metadataPillText && metadataPillAnchor && <AnnotationMetadataPill text={metadataPillText} anchorCoords={metadataPillAnchor} annotationRotation={rotationAngle} backgroundColor={accentColor} textColor={annotationLabel.style?.strokeColor} />}

      {isDrawing && !shouldShowVertexDuringDrawing && <Vertex key="vertex-0"
    // @ts-expect-error todo(ts-migration) TS2322 Type 'VertexT | undefined' is not assignable to type 'VertexT'.
    vertex={vertices[0]} disableDrag strokeColor={vertexStrokeColor} borderColor={vertexBorderColor} />}

      {isDrawing && shouldShowVertexDuringDrawing && vertices.map((vertex, index) => <Vertex key={`shape-vertex-${index}`} vertex={vertex} onSelect={() => {
      onVertexSelect && onVertexSelect(vertex, index);
    }} onDeselect={() => {
      onVertexDeselect?.(vertex, index);
    }} onMouseEnter={() => onVertexEnter?.(vertex, index)} onMouseLeave={() => onVertexLeave?.(vertex, index)} isRelationshipView={isAnnotationRelationshipMode} strokeColor={vertexStrokeColor} borderColor={vertexBorderColor} />)}

      {rotationAllowed && isSelected && isPointerSelected && <Vertex vertex={{
      point: rotationHandlerCoords,
      type: VERTEX_TYPE.ROTATE
    }} onSelect={() => {
      onVertexSelect && onVertexSelect({
        point: [...rotationHandlerCoords],
        type: VERTEX_TYPE.ROTATE
      }, 0);
    }} onDeselect={() => {
      onVertexDeselect?.({
        point: [...rotationHandlerCoords],
        type: VERTEX_TYPE.ROTATE
      }, 0);
    }} strokeColor={vertexStrokeColor} borderColor={vertexBorderColor} />}

      {annotationIdx !== null && isSelected && editorMode === EDITOR_MODE_TYPE.LABELING && vertices.map((vertex, index) => <Vertex key={`shape-${annotationIdx}-vertex-${index}`} vertex={vertex} onSelect={() => {
      onVertexSelect && onVertexSelect(vertex, index);
    }} onDeselect={() => {
      onVertexDeselect?.(vertex, index);
    }} onMouseEnter={() => onVertexEnter?.(vertex, index)} onMouseLeave={() => onVertexLeave?.(vertex, index)} isRelationshipView={isAnnotationRelationshipMode} strokeColor={vertexStrokeColor} borderColor={vertexBorderColor} />)}
      {annotationIdx !== null && isSelected && ghostVertices?.map((vertex, index) => <Vertex key={`shape-${annotationIdx}-vertex-${index}`} vertex={vertex} isGhostVertex={true} isRelationshipView={isAnnotationRelationshipMode} strokeColor={vertexStrokeColor} borderColor={vertexBorderColor} />)}
    </g>;
};
export default Shape;